// @generated-imports
import { playGame, showToast } from './app.js';
import { openBackupModal } from './backup.js';
import { CONFIG, state } from './core.js';
import { LOCAL_CORE_LABELS } from './dragdrop.js';
import { continueLocalRom, saveBiosFile } from './library.js';
// @generated-imports
/* ===========================================================================
   DIAGNÓSTICO COM AÇÃO (v2.0)
   ---------------------------------------------------------------------------
   Uma falha de carga deixa de ser "não foi possível iniciar": o portal diz
   ONDE parou (preflight, loader, core, ROM), POR QUÊ (404, BIOS, core,
   quota, offline, build) e O QUE FAZER (tentar de novo, importar BIOS,
   liberar espaço, copiar diagnóstico).
   ======================================================================== */

const DIAG_CODES = {
  'rom-404': { title: 'ROM não encontrada no servidor', hint: 'O arquivo apontado por romUrl não existe (HTTP 404). Confira o caminho no roms.json e se o arquivo foi publicado.' },
  'rom-forbidden': { title: 'Acesso à ROM negado', hint: 'O servidor respondeu 401/403. Verifique permissões da pasta roms/ ou regras do servidor.' },
  'rom-server': { title: 'Servidor com problema', hint: 'A ROM está no servidor, mas ele respondeu com erro (5xx). Tente novamente em instantes.' },
  offline: { title: 'Sem conexão', hint: 'O navegador está offline e este jogo ainda não está disponível offline.' },
  loader: { title: 'Emulador não baixou', hint: 'O loader do EmulatorJS não pôde ser carregado da build configurada.' },
  build: { title: 'Build do emulador inválida', hint: 'CONFIG.emulatorBuild não está na allowlist. Corrija a configuração.' },
  bios: { title: 'Este sistema precisa de BIOS', hint: 'O core exige um arquivo de BIOS que não está disponível. Você pode importar a BIOS do seu computador.' },
  core: { title: 'Falha no core do emulador', hint: 'O núcleo (WebAssembly) não iniciou. Pode ser incompatibilidade da build com este navegador.' },
  quota: { title: 'Sem espaço no navegador', hint: 'O armazenamento está cheio. Libere espaço no painel 💾 (cache é seguro apagar).' },
  timeout: { title: 'Tempo esgotado', hint: 'O emulador não respondeu no prazo. Rede lenta ou build travada.' },
  player: { title: 'Erro dentro do emulador', hint: 'O player reportou um erro inesperado.' },
  unknown: { title: 'Não foi possível iniciar', hint: 'Erro não classificado.' },
};

/** Classifica um erro de sessão em código + ações. */
function diagnoseLoadError(session, error) {
  const message = String((error && error.message) || error || '');
  const lower = message.toLowerCase();
  let code = (error && error.code) || null;
  if (!code) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) code = 'offline';
    else if (/http 404|não encontrada|not found/.test(lower)) code = 'rom-404';
    else if (/http 40[13]|acesso negado|forbidden|permiss/.test(lower)) code = 'rom-forbidden';
    else if (/http 5\d\d/.test(lower)) code = 'rom-server';
    else if (/loader/.test(lower)) code = 'loader';
    else if (/build/.test(lower)) code = 'build';
    else if (/bios/.test(lower)) code = 'bios';
    else if (/wasm|core|núcleo/.test(lower)) code = 'core';
    else if (/quota|storage|espaço/.test(lower)) code = 'quota';
    else if (/tempo limite|timeout/.test(lower)) code = 'timeout';
    else code = 'unknown';
  }
  const info = DIAG_CODES[code] || DIAG_CODES.unknown;
  const stage = lastStage(session);
  const details = buildDiagnosticText(session, code, message);
  const actions = [];
  if (code === 'offline' || code === 'rom-server' || code === 'timeout' || code === 'loader') {
    actions.push({ label: 'Tentar de novo', handler: () => retrySession(session) });
  }
  if (code === 'bios') {
    actions.push({ label: 'Importar BIOS local', handler: () => promptBiosImport(session.core) });
  }
  if (code === 'quota') {
    actions.push({ label: 'Abrir painel de dados', handler: () => openBackupModal() });
  }
  actions.push({ label: 'Copiar diagnóstico', handler: () => copyText(details, 'Diagnóstico copiado.') });
  return {
    code,
    title: info.title,
    message: `${info.title}. ${info.hint}${stage ? ` Parou em: ${stage}.` : ''}`,
    actions,
    details,
  };
}

function lastStage(session) {
  if (!session) return '';
  const stages = session.stagesFromPlayer || {};
  if (stages.start !== undefined) return 'jogo iniciado';
  if (stages.ready !== undefined) return 'emulador pronto';
  if (stages.rom !== undefined) return 'ROM entregue ao emulador';
  if (stages.loader !== undefined) return 'loader do emulador baixado';
  if (session.stages && session.stages.preflightEnd) return 'ROM validada (preflight ok), aguardando o emulador';
  if (session.stages && session.stages.preflightStart) return 'validação da ROM (preflight)';
  return 'antes de abrir o player';
}

function buildDiagnosticText(session, code, message) {
  const lines = [
    `EmuTab ${CONFIG.appVersion} — diagnóstico`,
    `Data: ${new Date().toISOString()}`,
    `Código: ${code}`,
    `Mensagem: ${message}`,
    `Build do emulador: ${CONFIG.emulatorBuild}`,
  ];
  if (session) {
    lines.push(`Sessão: ${session.id} (${session.kind})`);
    lines.push(`Jogo: ${session.title} · core ${session.core} · id ${session.gameId}`);
    if (session.romUrl) lines.push(`ROM: ${session.romUrl}`);
    const stages = Object.assign({}, session.stagesFromPlayer || {});
    if (session.stages) {
      if (session.stages.preflightStart && session.stages.preflightEnd) {
        stages.preflightMs = session.stages.preflightEnd - session.stages.preflightStart;
      }
    }
    lines.push(`Etapas (ms): ${JSON.stringify(stages)}`);
    lines.push(`Capacidades: ${JSON.stringify(session.capabilities || {})}`);
  }
  lines.push(`Online: ${typeof navigator !== 'undefined' ? navigator.onLine : '?'}`);
  lines.push(`Navegador: ${typeof navigator !== 'undefined' ? navigator.userAgent : '?'}`);
  return lines.join('\n');
}

function copyText(text, okMessage) {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(text)
      .then(() => showToast(okMessage || 'Copiado.'))
      .catch(() => showToast(text));
    return;
  }
  showToast(text);
}

function retrySession(session) {
  if (!session) return;
  if (session.kind === 'remote' && session.game) {
    state.preflightOk.delete(session.romUrl);
    playGame(session.game, session.options);
  } else if (session.kind === 'local' && session.gameId) {
    continueLocalRom(session.gameId);
  }
}

/* --- BIOS local: guardada por core na biblioteca e entregue ao player --- */

function promptBiosImport(core) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.bin,.rom,.zip,.img';
  input.addEventListener('change', async () => {
    const file = input.files && input.files[0];
    if (!file) return;
    const ok = await saveBiosFile(core, file);
    showToast(ok
      ? `BIOS guardada para ${LOCAL_CORE_LABELS[core] || core}. Tente abrir o jogo de novo.`
      : 'Não deu para guardar a BIOS neste navegador.');
  });
  input.click();
}

// @generated-exports
export { DIAG_CODES, buildDiagnosticText, copyText, diagnoseLoadError, lastStage, promptBiosImport, retrySession };
// @generated-exports
